
import { motion } from "framer-motion";
import { Heart, Share2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useWishlist } from "@/hooks/useWishlist";
import { Product } from "@/types/common";

interface PremiumProductActionButtonsProps {
  product: Product;
  isHovered: boolean;
}

const PremiumProductActionButtons = ({ product, isHovered }: PremiumProductActionButtonsProps) => {
  const { toast } = useToast();
  const { isInWishlist, addToWishlist, removeFromWishlist, isAddingToWishlist, isRemovingFromWishlist } = useWishlist();
  
  const isWishlisted = isInWishlist(product.id);
  const isLoading = isAddingToWishlist || isRemovingFromWishlist;

  const handleWishlistToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLoading) return;
    
    if (isWishlisted) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product.id);
    }
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (navigator.share) {
      navigator.share({
        title: product.name,
        text: `Check out this luxury item: ${product.name}`,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: "📋 Link Copied",
        description: "Product link copied to clipboard",
      });
    }
  };

  return (
    <div className="absolute top-4 right-4 z-20 flex flex-col gap-2">
      <motion.button
        onClick={handleWishlistToggle}
        disabled={isLoading}
        className="p-3 bg-background/90 backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
        whileHover={{ scale: isLoading ? 1 : 1.1 }}
        whileTap={{ scale: isLoading ? 1 : 0.9 }}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: isHovered ? 1 : 0, x: isHovered ? 0 : 20 }}
        transition={{ delay: 0.1 }}
      >
        {isLoading ? (
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : (
          <Heart 
            className={`h-4 w-4 transition-all duration-300 ${
              isWishlisted ? 'text-red-500 fill-red-500' : 'text-muted-foreground'
            }`}
          />
        )}
      </motion.button>

      <motion.button
        onClick={handleShare}
        className="p-3 bg-background/90 backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: isHovered ? 1 : 0, x: isHovered ? 0 : 20 }}
        transition={{ delay: 0.2 }}
      >
        <Share2 className="h-4 w-4 text-muted-foreground" />
      </motion.button>
    </div>
  );
};

export default PremiumProductActionButtons;
